'use client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { setCookie } from 'cookies-next';

import SuccessModal from '../SuccessModal';
import TextArea from '../Textarea';
import { Loading } from '../Loading';
import Button from '../Button';
import XIcon from '../Icons/X';
import Input from '../Input';

import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import { postShareWithFriends } from '@/actions/shareWithFriends';
import { yupResolver } from '@hookform/resolvers/yup';
import useLocalStorage from '@/hooks/useLocalStorage';
import { IExperience } from '@/types/Experience';
import { IVilla } from '@/types/Villa';

import { getTranslatedSchema } from './schema';
import { cn } from '@/lib/utils';
import { devError } from '@/utils/devLog';

type SendToFriendFormProps = {
  formDict: ISendToFriendsFormDictionary;
  onCancel?: () => void;
  isModal?: boolean;
  lang: Locale;
};

export default function SendToFriendForm({
  formDict,
  onCancel,
  lang,
  isModal = false,
}: SendToFriendFormProps) {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [villaShortList] = useLocalStorage('villas', [] as IVilla[]);
  const [experienceShortList] = useLocalStorage(
    'experience',
    [] as IExperience[],
  );

  const shortlist = {
    villas: villaShortList.map((item) => item.id),
    experience: experienceShortList.map((item) => item.id),
  };

  const shortlistString = JSON.stringify(shortlist);

  setCookie('shortlist', shortlistString, { maxAge: 60 * 60 * 24 * 7 });

  const schema = getTranslatedSchema();

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    const request = {
      emails: emailList,
      message: data.message,
    };

    try {
      if (emailList.length > 0) {
        setIsLoading(true);

        const response = await postShareWithFriends(request, lang);

        if (typeof response === 'boolean' && response) {
          setSuccessModalIsOpen(true);
          setEmailList([]);
          setValue('message', '');
          setShowError(false);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } else {
        setShowError(true);
        setIsLoading(false);
      }
    } catch (error) {
      devError(error);
    }
  });

  const addEmail = async () => {
    if (getValues('email')) {
      const newEmailList = [...(emailList || []), getValues('email') || ''];
      setEmailList(newEmailList);
      setValue('email', '');
    }
  };

  const removeEmail = async (email: string) => {
    if (!errors.email) {
      setEmailList(emailList.filter((e) => e !== email));
    }
  };

  return (
    <div
      className={`flex flex-col desktop:flex-row w-full h-auto mb-8 ${isModal && 'h-auto tablet:mt-[50px] -mb-8'
        }`}>
      {successModalIsOpen && (
        <SuccessModal
          modalIsOpen={successModalIsOpen}
          setModalIsOpen={setSuccessModalIsOpen}
          title={formDict.successModal.title}
          description={formDict.successModal.description}
        />
      )}

      <div
        className={`flex w-full flex-col overflow-y-auto h-[100vh] tablet:h-fit desktop:w-[90%] ${isModal &&
          'relative tablet:w-[90%] bg-white max-w-[1050px] mx-auto h-auto'
          }`}>
        {isLoading && (
          <div className="absolute flex w-full h-full justify-center items-center bg-[hsla(0,0%,100%,.6)] z-50">
            <Loading />
          </div>
        )}
        {isModal && (
          <div className="w-full tablet:px-[60px] pt-[30px] pb-4 flex flex-col text-center mt-4 items-center">
            <h2 className="text-4xl font-semibold text-center mb-2">
              {formDict.title}
            </h2>
            <p>{formDict.subtitle}</p>
          </div>
        )}
        <form
          className={`flex flex-col gap-4 w-full p-[30px] pt-0 tablet:px-[60px] tablet:pb-[60px]`}
          onSubmit={onSubmit}>
          <div className="flex flex-col desktop:flex-row gap-8">
            <div className="flex flex-col w-full desktop:w-1/2">
              <div className="flex flex-row">
                <Input.Container className="w-[calc(100%-2.75rem)]">
                  <Input.Label htmlFor="email">{formDict.email}</Input.Label>
                  <Input.Root
                    onChange={() => trigger('email')}
                    register={register}
                    name="email"
                  />
                  <Input.Error error={errors.email} />
                </Input.Container>
                <div className="flex flex-col">
                  <button
                    onClick={addEmail}
                    type="button"
                    className="bg-white transition duration-300 mt-[calc(1.8pc-4px)] w-[2.75rem] relative h-[2.75rem] border-[1px] group hover:bg-blue border-underline">
                    <span className="text-3xl absolute top-[-1px] left-1/2 -translate-x-1/2 transition duration-300 text-blue group-hover:text-white">
                      +
                    </span>
                  </button>
                </div>
              </div>
              <ul>
                {(emailList || []).map((email, index) => (
                  <li
                    key={index}
                    className="mb-2 flex flex-row items-center mt-4">
                    <button onClick={() => removeEmail(email)}>
                      <XIcon className="w-6 h-6 text-gray-600 fill-current mr-2" />
                    </button>
                    <span className="text-sm">{email}</span>
                  </li>
                ))}
              </ul>
            </div>
            <TextArea
              className="w-full desktop:w-1/2"
              inputClassName="w-full desktop:max-w-[443px] h-64"
              register={register}
              name="message"
              label={formDict.description}
            />
          </div>
          <div className="flex flex-col desktop:flex-row w-full">
            <p className="flex flex-1 justify-center text-warning text-center mb-4 desktop:mb-0">
              {showError && formDict.errorSend}
            </p>
            <div
              className={`flex justify-start relative tablet:justify-end ${isModal &&
                'flex-col-reverse tablet:justify-end gap-x-4 tablet:flex-row gap-y-2 '
                }`}>
              <Button
                type="button"
                onClick={() => onCancel && onCancel()}
                variant={'outline'}
                className={cn('w-full tablet:w-auto ml-auto tablet:ml-0', {
                  'mb-8 tablet:mb-0': /iPhone|iPad|iPod/i.test(
                    navigator.userAgent,
                  ),
                })}>
                {formDict.cancel}
              </Button>
              <Button
                type="submit"
                className={`w-full tablet:w-auto px-[30px] ${isModal && 'ml-auto tablet:ml-0'
                  }`}>
                {formDict.send}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
